import {
  MAX_ATTACHMENT_BYTES,
  base64ToBytes,
  decryptObject,
  encryptObject,
  utf8,
  type Bytes,
  type CryptoEnvelope,
} from "@securenotes/shared";

import { CSRF_HEADER_NAME } from "@securenotes/shared";

import { ApiError, apiRequest, readCsrfToken } from "../api/client";
import { attachmentMarkdown } from "../editor/attachments";

/**
 * Uploading an attachment (§7, §12, §15).
 *
 * The bytes are encrypted **here**, before they leave the browser, exactly as a note is:
 * AES-256-GCM under the DEK with a fresh 96-bit IV, and an AAD that binds the ciphertext
 * to this attachment and its key version. The worker stores an opaque object and never
 * sees plaintext.
 *
 * The envelope travels in the `metadata` part — the object type is `attachment_blob`,
 * which the frozen format already names for exactly this purpose. Without it the upload
 * would be unreadable, which is why the API requires it.
 */

export interface UploadedAttachment {
  id: string;
  markdown: string;
}

export interface AttachmentUploadInput {
  file: File;
  /** When the uploaded file should be removed, or null to keep it. The import keeps everything. */
  expiresAt?: number | null;
  /** The DEK, non-extractable. */
  dek: CryptoKey;
  keyVersion: number;
  /** Generated by the caller so the reference can be inserted before the upload lands. */
  attachmentId: string;
  headers?: Record<string, string>;
}

/** Encrypts attachment bytes and reports the envelope the API needs. */
export async function encryptAttachmentBytes(
  dek: CryptoKey,
  attachmentId: string,
  keyVersion: number,
  bytes: Bytes,
): Promise<{ ciphertext: Bytes; iv: string; plaintextSize: number }> {
  const envelope = await encryptObject(
    dek,
    {
      objectType: "attachment_blob",
      objectId: attachmentId,
      // Attachments are immutable: replacing one creates a new id, so there is no
      // revision to bind and the value is fixed.
      revision: 1,
      keyVersion,
    },
    bytes,
  );

  return {
    ciphertext: base64ToBytes(envelope.ciphertext),
    iv: envelope.iv,
    plaintextSize: bytes.byteLength,
  };
}

/**
 * Encrypts an attachment's filename.
 *
 * The filename is content like any other (§7): the server stores an envelope and never learns it. The object
 * type is `attachment_meta`, and the revision is fixed at 1 because attachments are immutable — replacing one
 * creates a new id.
 *
 * Getting this wrong is not a subtle failure: the server validates the metadata and rejects a plain string,
 * so every upload fails with a 400 and pasting an image silently does nothing.
 */
export async function encryptAttachmentName(
  dek: CryptoKey,
  attachmentId: string,
  keyVersion: number,
  name: string,
): Promise<CryptoEnvelope> {
  return encryptObject(
    dek,
    { objectType: "attachment_meta", objectId: attachmentId, revision: 1, keyVersion },
    utf8(name),
  );
}

/** Reads a filename back, for a caller that has the envelope and the key. */
export async function decryptAttachmentName(
  dek: CryptoKey,
  attachmentId: string,
  keyVersion: number,
  envelope: CryptoEnvelope,
): Promise<string> {
  const plaintext = await decryptObject(
    dek,
    { objectType: "attachment_meta", objectId: attachmentId, revision: 1, keyVersion },
    envelope,
  );
  return new TextDecoder().decode(plaintext);
}

/**
 * Uploads bytes that are **already encrypted**.
 *
 * Used for an attachment whose bytes were encrypted while the device was offline: the ciphertext was produced and
 * held then, and there is no reason to decrypt and re-encrypt merely to send it. The metadata is the same contract
 * the server validates either way.
 */
export async function uploadEncryptedAttachment(input: {
  attachmentId: string;
  ciphertext: Bytes;
  contentIv: string;
  plaintextSize: number;
  /** When the uploaded file should be removed, or null to keep it. */
  expiresAt?: number | null;
  name: CryptoEnvelope;
  contentType: string;
  csrfToken: string | null;
}): Promise<void> {
  const metadata = {
    id: input.attachmentId,
    name: input.name,
    contentType: input.contentType,
    // The ciphertext is what the server stores and counts, exactly as for a fresh upload.
    sizeBytes: input.ciphertext.byteLength,
    contentIv: input.contentIv,
    plaintextSizeBytes: input.plaintextSize,
    expiresAt: input.expiresAt ?? null,
  };

  const form = new FormData();
  form.set("metadata", JSON.stringify(metadata));
  form.set(
    "blob",
    new File([input.ciphertext as BlobPart], "blob", { type: "application/octet-stream" }),
  );

  const headers = new Headers();
  if (input.csrfToken !== null) {
    headers.set(CSRF_HEADER_NAME, input.csrfToken);
  }
  const response = await fetch("/api/v1/attachments", { method: "POST", body: form, headers });
  if (!response.ok) {
    throw new ApiError(
      response.status === 401 ? "UNAUTHENTICATED" : "INTERNAL",
      response.status,
      "the stored attachment could not be uploaded",
    );
  }
}

export async function uploadAttachment(input: AttachmentUploadInput): Promise<UploadedAttachment> {
  const { file, dek, keyVersion, attachmentId, expiresAt = null } = input;

  // `Bytes` is the WebCrypto-compatible view the shared crypto helpers require.
  const bytes = new Uint8Array(await file.arrayBuffer()) as Bytes;
  if (bytes.byteLength === 0) {
    throw new Error("The file is empty.");
  }
  if (bytes.byteLength > MAX_ATTACHMENT_BYTES) {
    throw new Error(
      `Each file must be under ${Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024))} MB.`,
    );
  }

  const { ciphertext, iv, plaintextSize } = await encryptAttachmentBytes(
    dek,
    attachmentId,
    keyVersion,
    bytes,
  );

  const metadata = {
    id: attachmentId,
    // The envelope the server's contract requires, not the filename itself.
    name: await encryptAttachmentName(
      dek,
      attachmentId,
      keyVersion,
      file.name.length > 0 ? file.name : "attachment",
    ),
    contentType: file.type || "application/octet-stream",
    // The size the server stores and counts against the ceiling is the ciphertext.
    sizeBytes: ciphertext.byteLength,
    contentIv: iv,
    plaintextSizeBytes: plaintextSize,
    expiresAt: expiresAt ?? null,
  };

  const form = new FormData();
  form.set("metadata", JSON.stringify(metadata));
  form.set(
    "blob",
    new File([ciphertext as BlobPart], "blob", { type: "application/octet-stream" }),
  );

  // The CSRF header is required for any unsafe method, and a raw `fetch` does not inherit
  // it from the JSON client — omitting it made every upload fail.
  const headers = new Headers(input.headers);
  const csrf = readCsrfToken();
  if (csrf !== null) {
    headers.set(CSRF_HEADER_NAME, csrf);
  }

  const response = await fetch("/api/v1/attachments", {
    method: "POST",
    body: form,
    headers,
    credentials: "same-origin",
  });

  const payload = (await response.json().catch(() => null)) as
    | { ok: true; data: { attachment: { id: string } } }
    | { ok: false; error: { code: string } }
    | null;

  if (response.status === 401) {
    // Uploading needs a live session even when the app was unlocked offline with the
    // device key, so this is a distinct outcome the caller must surface by name.
    throw new ApiError("UNAUTHENTICATED", 401);
  }
  if (response.status === 413) {
    throw new Error(
      `Each file must be under ${Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024))} MB.`,
    );
  }
  if (!response.ok || !payload || payload.ok !== true) {
    const code = payload && payload.ok === false ? payload.error.code : "INTERNAL";
    throw new ApiError(
      code === "PAYLOAD_TOO_LARGE" ? "PAYLOAD_TOO_LARGE" : "INTERNAL",
      response.status,
    );
  }

  return {
    id: payload.data.attachment.id,
    markdown: attachmentMarkdown(payload.data.attachment.id, file.name, file.type),
  };
}

/**
 * Links an attachment to a note, which is what raises its reference count (§12).
 *
 * Called when the reference is inserted, not when the upload finishes: an uploaded
 * object that no note references would be deleted by the sweeper as an orphan.
 */
export async function linkNoteAttachment(noteId: string, attachmentId: string): Promise<void> {
  await apiRequest(`/notes/${noteId}/attachments`, {
    method: "POST",
    body: { attachmentId },
  });
}

/**
 * Removes a reference; at zero references the server enqueues the object for deletion.
 *
 * The deletion is deliberately not performed here — removing a reference and destroying
 * the ciphertext are different decisions, and the sweep is idempotent.
 */
export async function unlinkNoteAttachment(noteId: string, attachmentId: string): Promise<void> {
  await apiRequest(`/notes/${noteId}/attachments/${attachmentId}`, { method: "DELETE" });
}

/**
 * Brings the server's references in line with the note's text.
 *
 * The text is the authority: an attachment whose reference was deleted is unlinked, and
 * one that appears is linked. Nothing happens for an id that is added and removed in the
 * same edit, which is what `diffAttachmentRefs` already decides.
 */
export async function syncAttachmentLinks(
  noteId: string,
  added: readonly string[],
  removed: readonly string[],
): Promise<{ linked: number; unlinked: number }> {
  for (const id of added) {
    await linkNoteAttachment(noteId, id);
  }
  for (const id of removed) {
    // Best effort per id: one stale reference must not prevent the rest from being
    // cleaned up.
    await unlinkNoteAttachment(noteId, id).catch(() => undefined);
  }
  return { linked: added.length, unlinked: removed.length };
}

/**
 * An attachment's alt text never carries note content: it is the filename the user
 * chose, and nothing else about the note leaks into a value a reader of the DOM can see.
 */
export const ATTACHMENT_ALT_SOURCE = utf8("attachment");
